import React, {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from 'react'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { InputNumber } from 'primereact/inputnumber'
import { Dropdown } from 'primereact/dropdown'
import { Checkbox } from 'primereact/checkbox'
import { Toast } from 'primereact/toast'
import server from '../../server'
import { ToastPopUp } from '../shared/utils'

const emptyRow = () => ({
  id: null,
  retailUpcCode: '',
  drugIdentificationNo: '',
  manufacturerId: null,
  itemDescription: '',
  strength: '',
  discontinued: false,
  backlog: false,
  sellingPrice: null,
  sellingPriceUnit: '',
  sellingPricePRText: '',
  packSize: null,
  minimumOrderQuantity: null,
  multipleOrderQuantity: null,
  maxOrderQuantityPerOrder: null,
  itemTypeCode: null,
  itemTypeShortDescription: '',
  itemTypeDescription: '',
  itemGroupMajorCode: '',
  itemGroupMajorDescription: '',
  itemGroupMinorCode: null,
  itemGroupMinorDescription: '',
  defaultSupplier: '',
  narcoticCode: null,
  priceFileType: '',
  provincialPriceListCode: '',
  provincialCode: '',
  itemCode: null,
  supplierId: null,
  sellingConversionFactor: null,
})

const AddEditCatalogueItem = forwardRef((props, ref) => {
  const { AddEditRef } = ref
  const [visible, setVisible] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [row, setRow] = useState(emptyRow())
  const [manufacturers, setManufacturers] = useState([])
  const toast = useRef(null)

  const loadManufacturers = () => {
    server
      .get('/manufacturers', { params: { page: 1, limit: 500 } })
      .then((res) => {
        const list = res.data?.Manufacturers || res.data?.data || []
        const arr = Array.isArray(list) ? list : []
        setManufacturers(arr.map((m) => ({ label: m.name, value: m.id })))
      })
      .catch(() => {
        setManufacturers([])
      })
  }

  useEffect(() => {
    if (visible) loadManufacturers()
  }, [visible])

  useImperativeHandle(AddEditRef, () => ({
    openNew() {
      setEditMode(false)
      setRow(emptyRow())
      setVisible(true)
    },
    editRow(r) {
      setEditMode(true)
      const spr = r.sellingPricePR
      let sellingPricePRText = ''
      if (spr != null) {
        sellingPricePRText =
          typeof spr === 'string' ? spr : JSON.stringify(spr, null, 2)
      }
      setRow({
        ...emptyRow(),
        ...r,
        drugIdentificationNo:
          r.drugIdentificationNo != null ? String(r.drugIdentificationNo) : '',
        sellingPricePRText,
      })
      setVisible(true)
    },
  }))

  const setField = (key, val) => {
    setRow((prev) => ({ ...prev, [key]: val }))
  }

  const parseDin = (v) => {
    if (v == null || v === '') return null
    const n = parseInt(String(v).replace(/\D/g, ''), 10)
    return Number.isNaN(n) ? null : n
  }

  const buildPayload = () => {
    let sellingPricePR = undefined
    if (row.sellingPricePRText && row.sellingPricePRText.trim()) {
      try {
        sellingPricePR = JSON.parse(row.sellingPricePRText)
      } catch {
        throw new Error('Province pricing (JSON) is invalid.')
      }
    } else if (editMode && row.sellingPricePRText === '') {
      sellingPricePR = null
    }
    const payload = {
      retailUpcCode: row.retailUpcCode,
      drugIdentificationNo: parseDin(row.drugIdentificationNo),
      manufacturerId: row.manufacturerId,
      itemDescription: row.itemDescription || null,
      strength: row.strength || null,
      discontinued: !!row.discontinued,
      backlog: !!row.backlog,
      sellingPrice:
        row.sellingPrice === '' || row.sellingPrice == null
          ? null
          : Number(row.sellingPrice),
      sellingPriceUnit: row.sellingPriceUnit || null,
      packSize: row.packSize,
      minimumOrderQuantity: row.minimumOrderQuantity,
      multipleOrderQuantity: row.multipleOrderQuantity,
      maxOrderQuantityPerOrder: row.maxOrderQuantityPerOrder,
      itemTypeCode: row.itemTypeCode,
      itemTypeShortDescription: row.itemTypeShortDescription || null,
      itemTypeDescription: row.itemTypeDescription || null,
      itemGroupMajorCode: row.itemGroupMajorCode || null,
      itemGroupMajorDescription: row.itemGroupMajorDescription || null,
      itemGroupMinorCode: row.itemGroupMinorCode,
      itemGroupMinorDescription: row.itemGroupMinorDescription || null,
      defaultSupplier: row.defaultSupplier || null,
      narcoticCode: row.narcoticCode,
      priceFileType: row.priceFileType || null,
      provincialPriceListCode: row.provincialPriceListCode || null,
      provincialCode: row.provincialCode || null,
      itemCode: row.itemCode,
      supplierId: row.supplierId,
      sellingConversionFactor: row.sellingConversionFactor,
    }
    if (sellingPricePR !== undefined) payload.sellingPricePR = sellingPricePR
    return payload
  }

  const save = () => {
    try {
      if (!editMode && !String(row.retailUpcCode || '').trim()) {
        ToastPopUp(toast, 'warn', 'UPC required', 'Retail UPC is required.')
        return
      }
      const payload = buildPayload()
      const req = editMode
        ? server.patch(`/admin/catalogue-items/${row.id}`, payload)
        : server.post('/admin/catalogue-items', payload)
      req
        .then(() => {
          ToastPopUp(
            toast,
            'success',
            editMode ? 'Updated' : 'Created',
            editMode ? 'Catalogue row saved.' : 'Catalogue row created.'
          )
          setVisible(false)
          props.getData()
        })
        .catch((e) => {
          ToastPopUp(
            toast,
            'error',
            'Error',
            e.response?.data?.message || 'Save failed'
          )
        })
    } catch (e) {
      ToastPopUp(toast, 'error', 'Validation', e.message)
    }
  }

  const footer = (
    <>
      <Button label='Cancel' icon='pi pi-times' outlined onClick={() => setVisible(false)} />
      <Button label='Save' icon='pi pi-check' onClick={save} />
    </>
  )

  const manufacturerItemTemplate = (option) =>
    option != null ? option.label ?? '' : ''

  const manufacturerValueTemplate = (option) => {
    if (option != null && option.label != null && option.label !== '') {
      return option.label
    }
    if (row.manufacturer?.name) return row.manufacturer.name
    const id = row.manufacturerId
    if (id == null) {
      return (
        <span className='text-muted'>Select manufacturer</span>
      )
    }
    const found = manufacturers.find((m) => m.value === id)
    return found?.label ?? `Manufacturer #${id}`
  }

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={visible}
        onHide={() => setVisible(false)}
        header={editMode ? 'Edit catalogue row' : 'New catalogue row'}
        style={{ width: '44rem', maxWidth: '95vw' }}
        modal
        footer={footer}
        className='catalogue-admin-dialog'
      >
        <div
          className='d-flex flex-column gap-2'
          style={{ maxHeight: '70vh', overflowY: 'auto' }}
        >
          <div className='row g-2'>
            <div className='col-md-6'>
              <label className='form-label'>Retail UPC {!editMode && '*'}</label>
              <InputText
                className='w-100'
                value={row.retailUpcCode || ''}
                onChange={(e) => setField('retailUpcCode', e.target.value)}
                disabled={editMode}
              />
            </div>
            <div className='col-md-6'>
              <label className='form-label'>DIN</label>
              <InputText
                className='w-100'
                value={row.drugIdentificationNo}
                onChange={(e) =>
                  setField(
                    'drugIdentificationNo',
                    e.target.value.replace(/\D/g, '').slice(0, 8)
                  )
                }
              />
            </div>
            <div className='col-12'>
              <label className='form-label'>Manufacturer</label>
              <Dropdown
                value={row.manufacturerId}
                options={manufacturers}
                onChange={(e) => setField('manufacturerId', e.value)}
                placeholder='Select manufacturer'
                filter
                showClear
                className='w-100'
                optionLabel='label'
                optionValue='value'
                itemTemplate={manufacturerItemTemplate}
                valueTemplate={manufacturerValueTemplate}
              />
            </div>
            <div className='col-12'>
              <label className='form-label'>Item description</label>
              <InputTextarea
                className='w-100'
                rows={2}
                value={row.itemDescription || ''}
                onChange={(e) => setField('itemDescription', e.target.value)}
              />
            </div>
            <div className='col-12'>
              <label className='form-label'>Strength</label>
              <InputTextarea
                className='w-100'
                rows={2}
                value={row.strength || ''}
                onChange={(e) => setField('strength', e.target.value)}
              />
            </div>
            <div className='col-md-4 d-flex align-items-center gap-2 pt-3'>
              <Checkbox
                inputId='disc'
                checked={!!row.discontinued}
                onChange={(e) => setField('discontinued', e.checked)}
              />
              <label htmlFor='disc'>Discontinued</label>
            </div>
            <div className='col-md-4 d-flex align-items-center gap-2 pt-3'>
              <Checkbox
                inputId='backlog'
                checked={!!row.backlog}
                onChange={(e) => setField('backlog', e.checked)}
              />
              <label htmlFor='backlog'>Backlog</label>
            </div>
            <div className='col-md-4'>
              <label className='form-label'>Selling price</label>
              <InputNumber
                className='w-100'
                value={row.sellingPrice}
                onValueChange={(e) => setField('sellingPrice', e.value)}
                mode='decimal'
                minFractionDigits={0}
                maxFractionDigits={4}
              />
            </div>
            <div className='col-md-4'>
              <label className='form-label'>Price unit</label>
              <InputText
                className='w-100'
                value={row.sellingPriceUnit || ''}
                onChange={(e) => setField('sellingPriceUnit', e.target.value)}
              />
            </div>
            <div className='col-12'>
              <label className='form-label'>Province prices (JSON)</label>
              <InputTextarea
                className='w-100 font-monospace'
                rows={5}
                value={row.sellingPricePRText || ''}
                onChange={(e) => setField('sellingPricePRText', e.target.value)}
                placeholder='{"ON": 10.5, "BC": null, ...}'
              />
            </div>
            <div className='col-md-6'>
              <label className='form-label'>Pack size</label>
              <InputNumber
                className='w-100'
                value={row.packSize}
                onValueChange={(e) => setField('packSize', e.value)}
                useGrouping={false}
              />
            </div>
            <div className='col-md-6'>
              <label className='form-label'>Min order qty</label>
              <InputNumber
                className='w-100'
                value={row.minimumOrderQuantity}
                onValueChange={(e) => setField('minimumOrderQuantity', e.value)}
                useGrouping={false}
              />
            </div>
            <div className='col-md-6'>
              <label className='form-label'>Multiple order qty</label>
              <InputNumber
                className='w-100'
                value={row.multipleOrderQuantity}
                onValueChange={(e) => setField('multipleOrderQuantity', e.value)}
                useGrouping={false}
              />
            </div>
            <div className='col-md-6'>
              <label className='form-label'>Max qty per order</label>
              <InputNumber
                className='w-100'
                value={row.maxOrderQuantityPerOrder}
                onValueChange={(e) => setField('maxOrderQuantityPerOrder', e.value)}
                useGrouping={false}
                placeholder='No limit'
              />
              <div className='form-text'>Leave empty for no limit.</div>
            </div>
            <div className='col-md-6'>
              <label className='form-label'>Item group major</label>
              <InputText
                className='w-100'
                value={row.itemGroupMajorDescription || ''}
                onChange={(e) =>
                  setField('itemGroupMajorDescription', e.target.value)
                }
              />
            </div>
            <div className='col-md-6'>
              <label className='form-label'>Item group minor</label>
              <InputText
                className='w-100'
                value={row.itemGroupMinorDescription || ''}
                onChange={(e) =>
                  setField('itemGroupMinorDescription', e.target.value)
                }
              />
            </div>
            <div className='col-md-4'>
              <label className='form-label'>Narcotic code</label>
              <InputNumber
                className='w-100'
                value={row.narcoticCode}
                onValueChange={(e) => setField('narcoticCode', e.value)}
                useGrouping={false}
              />
            </div>
            <div className='col-md-4'>
              <label className='form-label'>Item code</label>
              <InputNumber
                className='w-100'
                value={row.itemCode}
                onValueChange={(e) => setField('itemCode', e.value)}
                useGrouping={false}
              />
            </div>
            <div className='col-md-4'>
              <label className='form-label'>Item type code</label>
              <InputNumber
                className='w-100'
                value={row.itemTypeCode}
                onValueChange={(e) => setField('itemTypeCode', e.value)}
                useGrouping={false}
              />
            </div>
          </div>
        </div>
      </Dialog>
    </>
  )
})

export default AddEditCatalogueItem
